import { useQuery } from '@apollo/client/react';
import { useLingui } from '@lingui/react/macro';
import { styled } from '@linaria/react';
import { isDefined } from 'app-shared/utils';
import { IconAlertTriangle, IconLogout } from 'app-ui/icon';
import { Button } from 'app-ui/primitives/input';
import { themeCssVariables } from 'app-ui/theme';
import { Outlet } from 'react-router-dom';

import { SKIP_ERROR_TOAST_CONTEXT } from '@/apollo/constants/SkipErrorToastContext';
import { getUserFriendlyErrorMessage } from '@/apollo/utils/getUserFriendlyErrorMessage';
import { isSignedOutState } from '@/session/states/isSignedOutState';
import { EmptyState } from '@/ui/feedback/empty-state/components/EmptyState';
import { LoadingState } from '@/ui/feedback/loading-state/components/LoadingState';
import { useAtomStateValue } from '@/ui/utilities/state/jotai/hooks/useAtomStateValue';
import { GetCurrentSessionDocument } from '~/generated/graphql';

const StyledScreen = styled.div`
  background: ${themeCssVariables.background.primary};
  display: flex;
  flex: 1;
  flex-direction: column;
`;

// Loads who is signed in and their workspace before any page shows, so
// screens below read them without waiting (useCurrentUser,
// useCurrentWorkspace). Clerk's sign-in gate will sit in front of it.
export const SessionGate = () => {
  const { t } = useLingui();
  const isSignedOut = useAtomStateValue(isSignedOutState);
  const { data, loading, error, refetch } = useQuery(
    GetCurrentSessionDocument,
    // This gate shows its own full-page error.
    { context: SKIP_ERROR_TOAST_CONTEXT },
  );

  const handleRetryClick = () => {
    // A failed retry shows through `error`; the promise has nothing to add.
    refetch().catch(() => undefined);
  };

  const handleReloadClick = () => {
    window.location.reload();
  };

  if (isSignedOut) {
    return (
      <StyledScreen>
        <EmptyState
          Icon={IconLogout}
          title={t`You're signed out`}
          description={t`Your session has ended. Reload the page to sign in again.`}
          action={<Button onClick={handleReloadClick}>{t`Reload page`}</Button>}
        />
      </StyledScreen>
    );
  }

  if (isDefined(data)) {
    return <Outlet />;
  }

  if (isDefined(error)) {
    return (
      <StyledScreen>
        <EmptyState
          role="alert"
          Icon={IconAlertTriangle}
          title={t`Couldn't load your workspace`}
          description={
            getUserFriendlyErrorMessage(error) ??
            t`The server didn't answer. Check your connection, then try again.`
          }
          action={
            <Button loading={loading} onClick={handleRetryClick}>
              {t`Try again`}
            </Button>
          }
        />
      </StyledScreen>
    );
  }

  return (
    <StyledScreen>
      <LoadingState />
    </StyledScreen>
  );
};
